import React, {useRef} from 'react'
import {Control, Controller, FieldErrors, FieldValues, Path} from 'react-hook-form'
import {getFieldError} from './getFieldError'

type CurrencyInputProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  errors?: FieldErrors<T>
  label?: string
  placeholder?: string
  helperText?: string
  required?: boolean
  className?: string
  inputClassName?: string
  prefix?: string
  layout?: 'stacked' | 'half'
  disabled?: boolean
}

const normalizeCurrencyValue = (value: string): string => {
  const sanitizedValue = value.replace(/,/g, '').replace(/[^\d.]/g, '')
  const decimalIndex = sanitizedValue.indexOf('.')
  const wholeValue = (
    decimalIndex === -1 ? sanitizedValue : sanitizedValue.slice(0, decimalIndex)
  ).replace(/^0+(?=\d)/, '')

  if (decimalIndex === -1) return wholeValue

  const decimalValue = sanitizedValue
    .slice(decimalIndex + 1)
    .replace(/\./g, '')
    .slice(0, 2)

  return `${wholeValue || '0'}.${decimalValue}`
}

const formatCurrencyValue = (value: unknown): string => {
  const normalizedValue = normalizeCurrencyValue(String(value ?? ''))
  if (!normalizedValue) return ''

  const [wholeValue, decimalValue] = normalizedValue.split('.')
  const formattedWholeValue = wholeValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  return normalizedValue.includes('.')
    ? `${formattedWholeValue}.${decimalValue}`
    : formattedWholeValue
}

export const CurrencyInput = <T extends FieldValues>({
  name,
  control,
  errors,
  label,
  placeholder,
  helperText = '',
  required = false,
  className = '',
  inputClassName = '',
  prefix = '$',
  layout = 'stacked',
  disabled = false,
}: CurrencyInputProps<T>) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const error = getFieldError(errors, name)
  const wrapperClassName = layout === 'half' ? 'col-12 col-md-6 mb-0' : 'mb-5'

  return (
    <div className={`${wrapperClassName} ${className}`.trim()}>
      {label && (
        <label className='form-label fw-semibold' htmlFor={String(name)}>
          {label} {required && <span className='text-danger'>*</span>}
        </label>
      )}

      <div style={prefix ? {position: 'relative'} : undefined}>
        {prefix && (
          <span
            aria-hidden='true'
            style={{
              position: 'absolute',
              left: '0.9rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#8a94a6',
              fontWeight: 600,
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            {prefix}
          </span>
        )}

        <Controller
          name={name}
          control={control}
          rules={{required}}
          render={({field}) => (
            <input
              id={String(name)}
              type='text'
              inputMode='decimal'
              autoComplete='off'
              placeholder={placeholder}
              className={`form-control ${prefix ? 'ps-9' : ''} ${inputClassName} ${
                error ? 'is-invalid' : ''
              }`.trim()}
              disabled={disabled}
              aria-invalid={Boolean(error)}
              name={field.name}
              ref={(element) => {
                inputRef.current = element
                field.ref(element)
              }}
              value={formatCurrencyValue(field.value)}
              onChange={(event) => {
                const cursorPosition = event.target.selectionStart ?? event.target.value.length
                const charactersBeforeCursor = event.target.value
                  .slice(0, cursorPosition)
                  .replace(/[^\d.]/g, '').length
                const normalizedValue = normalizeCurrencyValue(event.target.value)

                field.onChange(normalizedValue)

                requestAnimationFrame(() => {
                  const input = inputRef.current
                  if (!input) return

                  let nextCursorPosition = 0
                  let characterCount = 0
                  while (
                    nextCursorPosition < input.value.length &&
                    characterCount < charactersBeforeCursor
                  ) {
                    if (/[\d.]/.test(input.value[nextCursorPosition])) characterCount += 1
                    nextCursorPosition += 1
                  }

                  input.setSelectionRange(nextCursorPosition, nextCursorPosition)
                })
              }}
              onBlur={() => {
                const normalizedValue = normalizeCurrencyValue(String(field.value ?? ''))
                field.onChange(normalizedValue ? Number(normalizedValue) : '')
                field.onBlur()
              }}
            />
          )}
        />
      </div>

      {helperText && <small className='text-muted d-block mt-1 fs-6'>{helperText}</small>}
      {error && <div className='text-danger mt-1'>{error.message}</div>}
    </div>
  )
}
